import React from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import ProductThumb from './ProductThumb';
import { AskedBadge, ColorDot, DirectStoreBadge, HeatBadge, SeasonBadge } from './Badges';
import { ADD_BACK_LABEL, EXCLUDE_LABEL } from './exclusions';
import { FLAG_LABEL, displayTitle, orderThumbTarget, priceLabel, variantLabel } from './format';
import { SinsangLink, Sourcing } from './SinsangLinks';
import { C, MONO, RADIUS, SHADOW, TIER_BADGE, TIER_NOTE } from './tokens';
import { MAX_VARIANT_QTY } from './orderEdit';
import {
  ADD_VARIANT_BLOCKED, ADD_VARIANT_EMPTY, ADD_VARIANT_LABEL, ADD_VARIANT_NOTE, ADD_VARIANT_NO_LIST,
  ADD_VARIANT_TITLE, ADD_VARIANT_VETOED, RestockV2Addable, addableIsOrderable, addableStockLine,
} from './orderPlan';
import { RestockV2OrderLine, RestockV2OrderVariant, RestockV2ThumbTarget, TierKey } from './types';

// 這次不訂 is per-order; 不要這款 is for good. The words have to carry that difference on their own,
// because the two controls sit on the same row.
export const THIS_ORDER_OFF = '這次不訂';
export const EXCLUDE_HINT = '以後都不建議';
export const RESET_LINE_LABEL = '還原數量';
// A colour HE put on the line. Said on the chip, because 引擎 N above it is the engine's count and
// a row the engine never proposed must not be read as one it did.
export const ADDED_CHIP_MARK = '你加的';
export const REMOVE_ADDED_LABEL = '拿掉這個顏色';

export const TierBadge: React.FC<{ tier: TierKey }> = ({ tier }) => {
  const badge = TIER_BADGE[tier] || TIER_BADGE.core;
  const solid = tier === 'core';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block', fontSize: 10.5, fontWeight: 800, px: '6px', borderRadius: '99px',
        mb: '3px', whiteSpace: 'nowrap',
        bgcolor: solid ? badge.colour : badge.wash, color: solid ? '#fff' : badge.colour,
        ...(solid ? {} : { border: '1px solid', borderColor: badge.colour }),
      }}
    >
      {badge.label}
    </Box>
  );
};

interface OrderLineCardProps {
  line: RestockV2OrderLine;
  // The 進貨店家, or null when the line has none. Since 2026-09-19 the 訂貨 screen is ONE flat list
  // with no shop headings, so the shop rides on the card — as a plain label in the metadata row,
  // never in a warning colour: a line with no shop is a normal order line (Magnus 2026-09-18) and
  // 沒有分店家 is a placeholder heading, which `orderPlan.shopTag` has already turned into null.
  //
  // Rendered VERBATIM and never split. Six live tags have a per-product note typed into them
  // (「miller 薄荷綠是淺的 不是深的」), and that note tells the buyer what to check — so the chip
  // clamps to one line with the whole tag in `title`, rather than parsing a shop name out of it and
  // truncating 「avec design」 in the process.
  shop: string | null;
  // 不要這款 is in flight for this style. PERMANENT and shared with Adam; the persisted ones are not
  // on this screen at all. Different state, different look, different verb from the tick below.
  excluded: boolean;
  // 這次不訂 — he unticked it for THIS order. Written nowhere, reversible by ticking, and the only
  // thing the tick has ever meant to him. Until 2026-09-18 the tick called the veto above, which is
  // why a line he unticked vanished and could not be ticked back.
  omitted: boolean;
  pending: boolean;
  whyOpen: boolean;
  sourcing: Sourcing;
  // What the buyer set for each colour/size, and whether this line carries any of his numbers.
  qtyOf: (variant: RestockV2OrderVariant) => number;
  edited: boolean;
  pieces: number;
  // Every catalogue colour/size that is NOT already a stepper above — each with what the plan
  // actually knows about it, so nothing here can be ordered blind.
  addable: RestockV2Addable[];
  // false = this plan carries no variant list for the SKU at all (no 選品 table). The picker then
  // says so instead of rendering an empty list that looks like 「there are no other colours」.
  catalogKnown: boolean;
  // 「這款：辦公室 3 件」 — the style's owned pieces, at the grain the endpoint measures them.
  ownedLine: string;
  pickerOpen: boolean;
  addedOf: (variant: RestockV2OrderVariant) => boolean;
  onTogglePicker: () => void;
  onAddVariant: (sku: string, row: RestockV2Addable) => void;
  onRemoveVariant: (sku: string, variantId: number) => void;
  onToggleOrder: (sku: string) => void;
  onVeto: (sku: string) => void;
  onVariantQty: (sku: string, variant: RestockV2OrderVariant, next: number) => void;
  onResetLine: () => void;
  onWhy: () => void;
  onImage: (target: RestockV2ThumbTarget) => void;
}

const STATE_BADGE: Record<RestockV2Addable['state'], string> = {
  ready: '', blocked: ADD_VARIANT_BLOCKED, vetoed: ADD_VARIANT_VETOED,
};

// −／＋ on one colour. Big enough to hit with a thumb at the counter and unmistakably a pair, so the
// number between them reads as a quantity he owns rather than one the engine is telling him.
const Step: React.FC<{ text: string; label: string; disabled: boolean; onClick: () => void }> = ({
  text, label, disabled, onClick,
}) => (
  <Box
    component="button" type="button" onClick={onClick} disabled={disabled} aria-label={label}
    sx={{
      appearance: 'none', width: 26, height: 26, flex: 'none', lineHeight: 1, cursor: 'pointer',
      borderRadius: '7px', border: '1px solid', borderColor: C.line2, bgcolor: C.card,
      color: C.ink, fontSize: 15, fontWeight: 800, display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center',
      '&:disabled': { opacity: 0.35, cursor: 'default' },
    }}
  >
    {text}
  </Box>
);

// tick → photo → tier badge → name → 依據 (the engine's own evidence, always visible, because
// 「what is your 依據 for adding sku 3588?」 must be answered without opening anything) → price/SKU
// → 下單連結 → the size chips a buyer types into the store's LINE.
// A variant that cannot be ordered keeps its chip, crossed out, so nobody re-asks for it.
const OrderLineCard: React.FC<OrderLineCardProps> = ({
  line, shop, excluded, omitted, pending, whyOpen, sourcing, qtyOf, edited, pieces, addable,
  catalogKnown, ownedLine, pickerOpen, addedOf, onTogglePicker, onAddVariant, onRemoveVariant,
  onToggleOrder, onVeto, onVariantQty, onResetLine, onWhy, onImage,
}) => {
  // Only the ones he can actually add: a count that includes 不可訂 / 已排除 rows would promise
  // more than the picker delivers.
  const addableCount = addable.filter(addableIsOrderable).length;
  const price = priceLabel(line.price);
  const evidence = line.lane_reason || line.why;
  // Both mean 「not going to Korea in this order」, so both dim the row — but only one of them is a
  // decision about the style itself, and only that one says 已排除.
  const off = excluded || omitted;
  return (
    <Box
      component="article" data-sku={line.sku}
      sx={{
        bgcolor: off ? C.wash : C.card, border: '1px solid',
        borderColor: off ? C.line2 : C.line, borderRadius: `${RADIUS}px`,
        p: 1.1, boxShadow: off ? 'none' : SHADOW, mb: 0.9, opacity: off ? 0.62 : 1,
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {/* The tick is 「這次要訂」 and nothing else. It writes nothing, so it is never disabled by
            a request in flight — a tick that cannot be un-ticked was the whole complaint. */}
        <Checkbox
          size="small" checked={!off} onChange={() => onToggleOrder(line.sku)} disableRipple
          disabled={excluded} inputProps={{ 'aria-label': `要訂 ${line.sku}` }}
          sx={{ p: 0.25, alignSelf: 'flex-start', color: C.line2, '&.Mui-checked': { color: C.go } }}
        />
        <Box sx={{ filter: off ? 'grayscale(1)' : 'none', flex: 'none' }}>
          <ProductThumb target={orderThumbTarget(line)} width={74} height={99} radius={7} onOpen={onImage} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
            <TierBadge tier={line.tier} />
            <SeasonBadge value={line.season} />
            <AskedBadge asked={line.asked} />
            {/* Design A: 熱門款／溫款 with its store split, and 到貨直送門市 on a HOT style's lines. */}
            <HeatBadge level={line.heat?.level} railFace={line.heat?.rail_face} storage={line.heat?.storage} />
            <DirectStoreBadge on={line.direct_store} />
            {excluded && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block', fontSize: 10.5, fontWeight: 800, borderRadius: '99px',
                  px: '7px', mb: '3px', bgcolor: C.backWash, color: C.back, whiteSpace: 'nowrap',
                }}
              >
                已排除
              </Box>
            )}
            {/* Deliberately NOT 已排除: this one is only about today's order and the word has to say
                so, or the two states read as the same permanent thing again. */}
            {omitted && !excluded && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block', fontSize: 10.5, fontWeight: 800, borderRadius: '99px',
                  px: '7px', mb: '3px', bgcolor: C.paper2, color: C.ink2, whiteSpace: 'nowrap',
                  border: '1px solid', borderColor: C.line2,
                }}
              >
                {THIS_ORDER_OFF}
              </Box>
            )}
          </Box>
          <Typography
            sx={{
              fontSize: 15, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.3,
              textDecoration: off ? 'line-through' : 'none',
            }}
          >
            {displayTitle(line.title)}
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.ink2, mt: '2px' }}>{evidence}</Typography>
          {/* 「how many, which colour and size」 (Magnus 2026-09-30: 「not just 1」): the 組 this line
              buys, e.g. 試賣：黑色 一組 M·L·XL. Bold ink — it is the answer to the order, not a footnote. */}
          {line.run_note && (
            <Typography
              data-run-note={line.sku}
              sx={{ fontSize: 12, fontWeight: 700, color: C.ink, mt: '2px', lineHeight: 1.35 }}
            >
              {line.run_note}
            </Typography>
          )}
          {/* 「為什麼是這個顏色」 (Magnus 2026-09-19: 「古巴領短袖 mostly people buy navy but it is
              telling me to buy black」). The engine was RIGHT — nine navy already hang on the rail —
              and nothing on the card said so. Served ONLY when the answer is surprising, right under
              the 依據 it qualifies, so a dense screen gains one line and only where it earns it. */}
          {line.variant_note && (
            <Typography
              data-variant-note={line.sku}
              sx={{ fontSize: 11.5, color: C.ink3, mt: '2px', lineHeight: 1.35 }}
            >
              {line.variant_note}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', mt: '3px' }}>
            {price && <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 800 }}>{price}</Typography>}
            <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>{line.sku}</Typography>
            {typeof line.listed_days === 'number' && line.listed_days <= 45 && (
              <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>上架 {line.listed_days} 天</Typography>
            )}
            {/* The shop, where the heading used to be. Neutral paper chip, not a verb colour — it
                says where to buy it, it does not ask for anything to be done. */}
            {shop && (
              <Box
                component="span" data-shop={shop} title={shop} aria-label={`進貨店家 ${shop}`}
                sx={{
                  fontSize: 10.5, fontWeight: 800, color: C.ink2, bgcolor: C.paper2,
                  borderRadius: '99px', px: '7px', py: '1px', maxWidth: 200, minWidth: 0,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                {shop}
              </Box>
            )}
          </Box>
          <SinsangLink sourcing={sourcing} sku={line.sku} />
          {/* Only a flag with a zh-TW sentence is shown. An unlabelled flag used to print its raw
              key in warning ochre, and the backend's new `no_shop_group` made that visible: a line
              with no 進貨店家 tag is NORMAL (Magnus 2026-09-18) and it was wearing a chip reading
              「no_shop_group」 in the colour reserved for problems. The group heading 沒有分店家
              already says this, neutrally; an internal key shown to the owner says nothing. */}
          {(line.flags || []).filter((flag) => !!FLAG_LABEL[flag]).map((flag) => (
            <Box
              key={flag}
              sx={{
                display: 'inline-block', fontSize: 10.5, fontWeight: 800, borderRadius: '99px',
                px: '7px', mt: '6px', mr: '4px',
                bgcolor: flag === 'preferred' ? C.fetchWash : C.waitWash,
                color: flag === 'preferred' ? C.fetch : C.wait,
              }}
            >
              {FLAG_LABEL[flag] || flag}
            </Box>
          ))}
        </Box>
        <Box sx={{ textAlign: 'right', flex: 'none' }} aria-label={`${line.sku} 這次訂 ${pieces} 件`}>
          <Typography
            data-line-total={line.sku}
            sx={{
              fontFamily: MONO, fontSize: 27, fontWeight: 700, letterSpacing: '-.04em', lineHeight: 1,
              color: off ? C.ink3 : (edited ? C.wait : C.ink),
              textDecoration: off ? 'line-through' : 'none',
            }}
          >
            {pieces}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>件</Typography>
          {/* His number replaced the engine's, so the engine's is still shown — he is overriding a
              recommendation and should be able to see what he overrode. Only when the two actually
              DIFFER, though: raising one colour by two and dropping another by two lands back on
              the engine's own total, and 「5 · 引擎 5」 is noise pretending to be a correction. */}
          {edited && pieces !== line.order_qty && (
            <Typography sx={{ fontSize: 10, color: C.wait, fontWeight: 700, whiteSpace: 'nowrap' }}>
              引擎 {line.order_qty}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Magnus 2026-09-17: 「make it easy for me to change the variant to order」. Each colour is a
          stepper, and 0 is how a colour is dropped — one mechanism, so there is no second way to
          take a colour out that the copy text might not hear about. A blocked colour keeps its
          crossed-out chip and gets no stepper: the source cannot supply it, and a number he could
          type there would be one the order can never satisfy. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px', mt: 1.1 }}>
        {(line.variants || []).map((variant, index) => {
          const blocked = !!(variant.block_reason || '').trim();
          const qty = qtyOf(variant);
          const changed = qty !== (Number(variant.order_qty) || 0);
          const chip = variantLabel(variant.color, variant.size) || '單一款';
          const dropped = qty <= 0;
          const added = addedOf(variant);
          return (
            <Box
              key={`${variant.variant_id ?? 'x'}:${index}`}
              component="span" data-variant-chip={`${line.sku}|${variant.variant_id ?? ''}`}
              data-added={added ? 'true' : 'false'}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                fontSize: 12.5, fontWeight: 700, borderRadius: '8px', pl: '7px',
                pr: blocked ? '7px' : '3px', py: '3px',
                whiteSpace: 'nowrap', bgcolor: blocked ? '#FFF9F6' : (changed || added ? C.waitWash : C.wash),
                border: '1px solid',
                borderColor: blocked ? '#EBBFAF' : (changed || added ? C.wait : C.line),
                color: blocked ? C.ink3 : C.ink,
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex', alignItems: 'center',
                  textDecoration: blocked || off || dropped ? 'line-through' : 'none',
                  color: dropped && !blocked ? C.ink3 : 'inherit',
                }}
              >
                <ColorDot name={variant.color} size={9} />
                {chip}
              </Box>
              {/* It came from him, not from the engine, and the row above prints 引擎 N — so the
                  chip says whose number this is rather than letting the two read alike. */}
              {added && !blocked && (
                <Box component="span" sx={{ fontSize: 9.5, fontWeight: 800, color: C.wait, ml: '1px' }}>
                  {ADDED_CHIP_MARK}
                </Box>
              )}
              {blocked ? (
                <>
                  <Box component="span" sx={{ fontFamily: MONO, ml: '3px', textDecoration: 'line-through' }}>
                    ×{variant.order_qty}
                  </Box>
                  <Box
                    component="span"
                    sx={{ ml: '4px', fontSize: 10.5, color: C.back, display: 'inline-block' }}
                  >
                    {variant.block_reason}
                  </Box>
                </>
              ) : (
                <>
                  <Step
                    text="−" label={`${chip} 少一件 ${line.sku}`} disabled={off || qty <= 0}
                    onClick={() => onVariantQty(line.sku, variant, qty - 1)}
                  />
                  <Box
                    component="span" data-qty={`${line.sku}|${variant.variant_id ?? ''}`}
                    sx={{
                      fontFamily: MONO, fontSize: 14, fontWeight: 700, minWidth: 20,
                      textAlign: 'center', color: changed ? C.wait : C.ink,
                    }}
                  >
                    {qty}
                  </Box>
                  <Step
                    text="＋" label={`${chip} 多一件 ${line.sku}`} disabled={off || qty >= MAX_VARIANT_QTY}
                    onClick={() => onVariantQty(line.sku, variant, qty + 1)}
                  />
                  {/* Only a HAND-ADDED row can be taken off the line entirely. An engine row is
                      dropped by stepping it to 0 — one verb each, and neither can be confused for
                      the standing 不要這個顏色／尺寸 in 選品. */}
                  {added && (
                    <Box
                      component="button" type="button" disabled={off}
                      aria-label={`${REMOVE_ADDED_LABEL} ${chip} ${line.sku}`}
                      onClick={() => onRemoveVariant(line.sku, variant.variant_id as number)}
                      sx={{
                        appearance: 'none', border: 0, background: 'none', p: 0, ml: '2px', mr: '3px',
                        cursor: 'pointer', fontSize: 12, fontWeight: 800, color: C.back, lineHeight: 1,
                        '&:disabled': { opacity: 0.35, cursor: 'default' },
                      }}
                    >
                      ✕
                    </Box>
                  )}
                </>
              )}
            </Box>
          );
        })}
        {/* The opener sits at the END of the chip row, so the card looks exactly as it did until he
            reaches for it: a line whose colours are all already there costs one muted word. */}
        <Box
          component="button" type="button" onClick={onTogglePicker} disabled={off}
          aria-expanded={pickerOpen} aria-label={`${ADD_VARIANT_LABEL} ${line.sku}`}
          sx={{
            appearance: 'none', display: 'inline-flex', alignItems: 'center', gap: '2px',
            borderRadius: '8px', border: '1px dashed', borderColor: pickerOpen ? C.wait : C.line2,
            bgcolor: pickerOpen ? C.waitWash : 'transparent', color: pickerOpen ? C.wait : C.ink2,
            px: '7px', py: '3px', fontSize: 12, fontWeight: 800, cursor: 'pointer',
            whiteSpace: 'nowrap',
            '&:disabled': { opacity: 0.35, cursor: 'default' },
          }}
        >
          {/* The COUNT is the whole point of putting it here. Magnus 2026-09-20 asked how he could
              「add more 款式 to 門市 — 門市 has black L and i want to add ivory L blue L」, which
              is exactly what this button has always done; he could not tell it was a door. Every
              order line on the live plan has extras behind it (2899 shows 1 colour and hides 5),
              and a bare 「＋加顏色／尺寸」 reads like a form field rather than 「there are five
              more colours in here」. Orderable ones only — the number has to be the number of
              things he can actually act on, or it becomes another thing to distrust. */}
          ＋{ADD_VARIANT_LABEL}{addableCount > 0 && (
            <Box component="span" sx={{ fontFamily: MONO, fontWeight: 800, ml: '1px' }}>
              {addableCount}
            </Box>
          )}
        </Box>
      </Box>

      {pickerOpen && (
        <Box
          aria-label={`${ADD_VARIANT_TITLE} ${line.sku}`}
          sx={{
            mt: '6px', px: 1, py: 0.75, borderRadius: '9px', bgcolor: C.wash, border: '1px solid',
            borderColor: C.line2,
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: C.ink2 }}>{ADD_VARIANT_TITLE}</Typography>
          {ownedLine && (
            <Typography sx={{ fontSize: 11.5, color: C.fetch, fontWeight: 700, mt: '2px' }}>{ownedLine}</Typography>
          )}
          {!catalogKnown && (
            <Typography sx={{ fontSize: 11.5, color: C.back, mt: '3px' }}>{ADD_VARIANT_NO_LIST}</Typography>
          )}
          {catalogKnown && addable.length === 0 && (
            <Typography sx={{ fontSize: 11.5, color: C.ink3, mt: '3px' }}>{ADD_VARIANT_EMPTY}</Typography>
          )}
          {addable.map((row) => {
            const label = variantLabel(row.color, row.size) || '單一款';
            const ok = addableIsOrderable(row);
            return (
              <Box
                key={`add:${row.variant_id}`} data-addable={`${line.sku}|${row.variant_id}`}
                data-orderable={ok ? 'true' : 'false'}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, mt: '5px', pt: '5px',
                  borderTop: '1px solid', borderColor: C.line,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 12.5, fontWeight: 700 }}>
                      <ColorDot name={row.color} size={9} />
                      {label}
                    </Box>
                    {!ok && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 10, fontWeight: 800, borderRadius: '99px', px: '6px',
                          bgcolor: C.backWash, color: C.back, whiteSpace: 'nowrap',
                        }}
                      >
                        {STATE_BADGE[row.state]}
                      </Box>
                    )}
                  </Box>
                  {/* Always both: what is already here, and — when it cannot be ordered — why. */}
                  <Typography sx={{ fontSize: 11, color: C.ink3 }}>{addableStockLine(row)}</Typography>
                  {!ok && (
                    <Typography sx={{ fontSize: 11, color: C.back, fontWeight: 700 }}>{row.reason}</Typography>
                  )}
                </Box>
                <Box
                  component="button" type="button" disabled={!ok || off}
                  aria-label={`${ADD_VARIANT_LABEL} ${line.sku} ${label}`}
                  onClick={() => onAddVariant(line.sku, row)}
                  sx={{
                    appearance: 'none', flex: 'none', cursor: 'pointer', borderRadius: '7px',
                    border: '1px solid', borderColor: C.go, bgcolor: C.goWash, color: C.go,
                    px: '10px', py: '4px', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap',
                    '&:disabled': { opacity: 0.4, cursor: 'default', borderColor: C.line2, bgcolor: C.paper2, color: C.ink3 },
                  }}
                >
                  加入
                </Box>
              </Box>
            );
          })}
          {catalogKnown && addable.length > 0 && (
            <Typography sx={{ fontSize: 11, color: C.ink3, mt: '6px' }}>{ADD_VARIANT_NOTE}</Typography>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: '6px' }}>
        <Box
          component="button" type="button" onClick={onWhy} aria-expanded={whyOpen}
          aria-label={`為什麼要訂 ${line.sku}`}
          sx={{
            appearance: 'none', background: 'none', border: 0, p: 0, fontSize: 11.5, color: C.ink3,
            fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: '2px', cursor: 'pointer',
          }}
        >
          為什麼
        </Box>
        {/* 還原這款 only appears once there is something to restore, and it undoes his QUANTITIES,
            never his tick — two different changes, two different undos. */}
        {edited && (
          <Box
            component="button" type="button" onClick={onResetLine}
            aria-label={`${RESET_LINE_LABEL} ${line.sku}`}
            sx={{
              appearance: 'none', background: 'none', border: 0, p: 0, fontSize: 11.5,
              fontWeight: 800, cursor: 'pointer', color: C.wait, textDecoration: 'underline',
              textUnderlineOffset: '2px',
            }}
          >
            {RESET_LINE_LABEL}
          </Box>
        )}
        {/* The permanent one. A bordered, filled control rather than a bare underline, because it
            is a standing decision Adam sees too and it must not read like the tick beside it —
            until 2026-09-18 the two did the identical thing, which is what made the tick a trap. */}
        <Box
          component="button" type="button" onClick={() => onVeto(line.sku)} disabled={pending}
          aria-label={`${excluded ? ADD_BACK_LABEL : EXCLUDE_LABEL} ${line.sku}`}
          sx={{
            appearance: 'none', ml: 'auto', px: '9px', py: '3px', fontSize: 11.5, fontWeight: 800,
            cursor: 'pointer', borderRadius: '7px', border: '1px solid',
            borderColor: excluded ? C.go : C.back,
            bgcolor: excluded ? C.goWash : C.backWash, color: excluded ? C.go : C.back,
            '&:disabled': { opacity: 0.5, cursor: 'default' },
          }}
        >
          {excluded ? ADD_BACK_LABEL : EXCLUDE_LABEL}
          {!excluded && (
            <Box component="span" sx={{ fontWeight: 700, opacity: 0.8, ml: '4px' }}>
              {EXCLUDE_HINT}
            </Box>
          )}
        </Box>
      </Box>

      {whyOpen && (
        <Box
          sx={{
            mt: '5px', fontSize: 12, color: C.ink2, bgcolor: C.wash, border: '1px dashed',
            borderColor: C.line2, borderRadius: '7px', px: 1, py: 0.75,
          }}
        >
          {/* The shop is named only when there IS one — the same `shop` the chip above shows, so
              the two cannot disagree. `line.source_tag` carries the GROUP HEADING, so an untagged
              line would read 沒有分店家 here — a heading, not a supplier, and `orderPlan.shopTag`
              has already kept it out. A line with no shop is a normal order line (Magnus
              2026-09-18: the tag groups, the 下單連結 orders), so the fold simply says one less
              thing about it rather than flagging an absence as a problem. */}
          <Box sx={{ fontWeight: 800, mb: '3px' }}>
            {line.sku} · {line.cat}{shop ? ` · ${shop}` : ''}
          </Box>
          {/* The 依據 sentence is already on the card, always visible; the fold adds the numbers. */}
          <Box sx={{ color: C.ink3 }}>{TIER_NOTE[line.tier] || ''}</Box>
          <Box component="table" sx={{ mt: 0.75, width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
            <Box component="thead">
              <Box component="tr">
                {['顏色／尺寸', '現有', '目標', '訂'].map((head, index) => (
                  <Box
                    key={head} component="th"
                    sx={{
                      textAlign: index === 0 ? 'left' : 'right', px: 0.75, py: '3px', color: C.ink3,
                      fontWeight: 700, fontSize: 10.5, borderBottom: '1px solid', borderColor: C.line,
                    }}
                  >
                    {head}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {(line.variants || []).map((variant, index) => (
                <Box component="tr" key={`row:${variant.variant_id ?? 'x'}:${index}`}>
                  <Box component="td" sx={{ px: 0.75, py: '3px', borderBottom: '1px solid', borderColor: C.line }}>
                    {variantLabel(variant.color, variant.size) || '單一款'}
                  </Box>
                  {[variant.on_hand, variant.target, variant.order_qty].map((value, cell) => (
                    <Box
                      key={`cell:${cell}`} component="td"
                      sx={{
                        px: 0.75, py: '3px', textAlign: 'right', fontFamily: MONO,
                        borderBottom: '1px solid', borderColor: C.line,
                      }}
                    >
                      {value}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OrderLineCard;
